import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  ListGroup,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import CopyToClipboard from "react-copy-to-clipboard";
import {
  BsPencil,
  BsClipboard,
  BsMailbox,
  BsPrinter,
  BsXCircle,
  BsQuestionCircle,
  BsPerson,
  BsClipboardCheck,
  BsCaretDownFill,
  BsFileCode,
  BsArchive,
  BsChatSquareText,
  BsFiles,
  BsFillFilePdfFill,
} from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getBookingTextData } from "../../utils/text-data";
import { formatUTCDate, formatDateTime, formatUTCTime } from "../../utils/date";
import BookingQuickView from "../booking-quick-view/BookingQuickView";
import ConfirmModal from "../confirm-modal/ConfirmModal";
import { showSuccess } from "../toaster/store/slice";
import styles from "./bookingShow.module.scss";
import bookingShowSlice from "./store/slice";
import handlePrint from "../../utils/print";
import InternalBadge from "../internal-badge/InternalBadge";
import moment from "moment";
import StatusBadge from "../status-badge/StatusBadge";
import { hasRole } from "../../utils/roles";
import userSlice, { RoleCode } from "../users/store/slice";
import Select, { StylesConfig } from "react-select";
import { UserRequestParams } from "../users/store/saga";
import defaultSelectStyle from "../../utils/select";
import FilesModal from "../files-modal/FilesModal";
import {
  bookingIsCancelable,
  bookingIsEditable,
} from "../../utils/permissions";
import documentSlice, { Media } from "../documents/store/slice";
import LiveQueueBadge from "../live-queue-badge/LiveQueueBadge";
import CargoBadge from "../cargo-badge/CargoBadge";

type CustomerOption = {
  label: string;
  value: number;
  isDisabled?: boolean;
};

type Props = {
  handleClose: () => void;
};

const BookingShow = ({ handleClose }: Props) => {
  const { id } = useParams<string>();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [show, setShow] = useState(true);
  const [isCancelConfirmVisible, setIsCancelConfirmVisible] =
    useState<boolean>(false);
  const [isFilesVisible, setIsFilesVisible] = useState<boolean>(false);
  const [assignMode, setAssignMode] = useState(false);

  const profile = useSelector((state) => state.loginPage.profile);
  const data = useSelector((state) => state.bookingShow.data);
  const isLoading = useSelector((state) => state.bookingShow.isLoading);
  const isAssigning = useSelector((state) => state.bookingShow.isAssigning);
  const users = useSelector((state) => state.users.data);

  const fetchData = useCallback(
    (id: number) => dispatch(bookingShowSlice.actions.fetchData(id)),
    [dispatch]
  );
  const reset = useCallback(
    () => dispatch(bookingShowSlice.actions.reset()),
    [dispatch]
  );
  const cancel = useCallback(
    (id: number) => dispatch(bookingShowSlice.actions.cancel(id)),
    [dispatch]
  );
  const showSuccessText = useCallback(
    (text: string) => dispatch(showSuccess(text)),
    [dispatch]
  );
  const download = useCallback(
    (document: Media) => dispatch(documentSlice.actions.download(document)),
    [dispatch]
  );
  const fetchUsers = useCallback(
    (companyId: number) =>
      dispatch(
        userSlice.actions.fetch({
          roleCode: RoleCode.Customer,
          onlyWithCompanies: true,
          companyId,
        } as UserRequestParams)
      ),
    [dispatch]
  );
  const assign = useCallback(
    (bookingId: number, userId: number) =>
      dispatch(bookingShowSlice.actions.assign({ bookingId, userId })),
    [dispatch]
  );

  const handleGentleClose = () => {
    setShow(false);

    setTimeout(() => {
      handleClose();
    }, 100);
  };

  useEffect(() => {
    if (id) {
      fetchData(Number(id));
    }

    return () => {
      reset();
    };
  }, [id, fetchData, reset]);

  useEffect(() => {
    if (data && assignMode) {
      fetchUsers(data.company.id);
    }
  }, [assignMode, data, fetchUsers]);

  const isEditable = useMemo(
    () => bookingIsEditable(data, profile),
    [data, profile]
  );

  const isCancelable = useMemo(
    () => bookingIsCancelable(data, profile),
    [data, profile]
  );

  const bookingTextData = useMemo(
    () => (data ? getBookingTextData(data) : ""),
    [data]
  );

  const customerOptions: CustomerOption[] = useMemo(
    () =>
      data
        ? users.map((user) => ({
            label: user.name,
            value: user.id,
            isDisabled: user.id === data.owner.id,
          }))
        : [],
    [users, data]
  );

  if (!profile) return null;

  const isInternal = hasRole(profile, RoleCode.Internal);

  return (
    <>
      <Modal
        show={show}
        onHide={handleGentleClose}
        backdrop="static"
        size="xl"
        fullscreen="lg-down"
      >
        <Modal.Header closeVariant="white" closeButton>
          <Modal.Title className={styles.title}>
            <div className={styles.information}>
              <span>Booking information</span>
              <small className="text-secondary">Booking ID: {id}</small>
            </div>
            {data && (
              <Dropdown className={styles.dropdown}>
                <Dropdown.Toggle
                  variant="outline-primary"
                  id="dropdown-basic"
                  size="sm"
                >
                  Actions
                </Dropdown.Toggle>

                <Dropdown.Menu className={styles.rowMenu}>
                  <Dropdown.Item
                    onClick={() => {
                      setShow(false);

                      setTimeout(() => {
                        navigate(`edit`);
                      }, 100);
                    }}
                    disabled={!isEditable}
                  >
                    <BsPencil /> Edit
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setShow(false);

                      setTimeout(() => {
                        navigate(`pricing`);
                      }, 100);
                    }}
                    disabled={!isEditable || isInternal}
                  >
                    <BsQuestionCircle /> Ask price
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <CopyToClipboard
                    text={bookingTextData}
                    onCopy={() => showSuccessText("Copied to clipboard.")}
                  >
                    <Dropdown.Item>
                      <BsClipboard /> Copy to clipboard
                    </Dropdown.Item>
                  </CopyToClipboard>
                  <Dropdown.Item
                    href={`mailto:?subject=Booking ${
                      data.id
                    }&body=${encodeURIComponent(bookingTextData)}`}
                  >
                    <BsMailbox /> Send as mail
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handlePrint(bookingTextData)}>
                    <BsPrinter /> Print
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  {id && (
                    <Dropdown.Item
                      onClick={() => {
                        setShow(false);
                        setIsCancelConfirmVisible(true);
                      }}
                      className={styles.cancelBooking}
                      disabled={!isCancelable}
                    >
                      <BsXCircle /> Cancel booking
                    </Dropdown.Item>
                  )}
                  {isInternal && (
                    <>
                      <Dropdown.Divider />
                      <Dropdown.Item
                        onClick={() => {
                          setShow(false);
                          setIsFilesVisible(true);
                        }}
                      >
                        <BsFileCode /> Show files{" "}
                        <InternalBadge className={styles.showFilesBadge} />
                      </Dropdown.Item>
                    </>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container fluid>
            {!!(data && data.archived_at) && (
              <Row>
                <Col>
                  <Alert variant="info" className={styles.alert}>
                    <BsArchive /> Booking is archived.
                  </Alert>
                </Col>
              </Row>
            )}
            {!!(data && data.provider_notes) && (
              <Row>
                <Col>
                  <Alert variant="danger" className={styles.alert}>
                    <BsChatSquareText /> Provider note: {data.provider_notes}
                  </Alert>
                </Col>
              </Row>
            )}
            <Row className="gy-3">
              <Col xl={8}>
                <Row>
                  <Col>
                    <Card>
                      <Card.Body>
                        <Card.Title className={styles.summaryTitle}>
                          <div>
                            <BsClipboardCheck /> Summary
                          </div>
                          {data && (
                            <div className={styles.assignUser}>
                              <span>Assigned to:</span>

                              {assignMode ? (
                                <Select
                                  onChange={(option: any) => {
                                    assign(data.id, option.value);
                                    setAssignMode(false);
                                  }}
                                  onBlur={() => setAssignMode(false)}
                                  options={customerOptions}
                                  defaultValue={{
                                    label: data.owner.name,
                                    value: data.owner.id,
                                  }}
                                  placeholder="Search"
                                  styles={{
                                    ...(defaultSelectStyle as StylesConfig<CustomerOption>),
                                    input: (base) => ({
                                      ...base,
                                    }),
                                  }}
                                  autoFocus
                                  menuIsOpen
                                  isDisabled={!!data.archived_at}
                                />
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => setAssignMode(true)}
                                  disabled={isAssigning || !!data.archived_at}
                                >
                                  {data.owner.name} <BsCaretDownFill />
                                </Button>
                              )}

                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`assign-user-tooltip`}>
                                    Assigned user will be able to edit booking
                                    and receive email notifications.
                                  </Tooltip>
                                }
                              >
                                <span className={styles.assignQuestion}>
                                  <BsQuestionCircle />
                                </span>
                              </OverlayTrigger>
                            </div>
                          )}
                        </Card.Title>

                        <hr />

                        {!!(isLoading && !data) && (
                          <Spinner
                            className={`text-primary`}
                            animation="border"
                          />
                        )}
                        {data && (
                          <ListGroup
                            className={styles.bookingData}
                            variant="flush"
                          >
                            {data.booking_status && (
                              <ListGroup.Item>
                                <Row>
                                  <Col>Booking status</Col>{" "}
                                  <Col>
                                    <StatusBadge status={data.booking_status} />
                                  </Col>
                                </Row>
                              </ListGroup.Item>
                            )}
                            <ListGroup.Item>
                              <Row>
                                <Col>Ticket status</Col>{" "}
                                <Col>
                                  <StatusBadge status={data.ticket_status} />
                                </Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Invoice status</Col>{" "}
                                <Col>
                                  <StatusBadge
                                    status={data.invoice_status}
                                    showDocumentIcon={data.documents.length > 0}
                                  />
                                </Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Departure country</Col>{" "}
                                <Col>{data.route.departure_country.name}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Arrival country</Col>{" "}
                                <Col>{data.route.arrival_country.name}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Route</Col>{" "}
                                <Col>{data.route.description}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Departure date</Col>{" "}
                                <Col>{formatUTCDate(data.departure_at)}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Departure time</Col>{" "}
                                <Col>
                                  {formatUTCTime(data.departure_at)}{" "}
                                  {data.route.is_transit && (
                                    <LiveQueueBadge
                                      validityDays={data.route.validity_days}
                                    />
                                  )}
                                </Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Arrival date</Col>{" "}
                                <Col>{formatUTCDate(data.arrival_at)}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Arrival time</Col>{" "}
                                <Col>{formatUTCTime(data.arrival_at)}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Duration</Col>{" "}
                                <Col>
                                  {moment(data.duration, "HH:mm:ss").format(
                                    "HH:mm"
                                  )}
                                </Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>ADR</Col>{" "}
                                <Col>
                                  {data.adr !== null
                                    ? data.adr
                                      ? "Yes"
                                      : "No"
                                    : "-"}
                                </Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Vehicle registration</Col>{" "}
                                <Col>{data.vehicle_number ?? "-"}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Vehicle registration country</Col>{" "}
                                <Col>
                                  {data.vehicle_registration_country?.name ??
                                    "-"}
                                </Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Trailer registration</Col>{" "}
                                <Col>{data.trailer_number ?? "-"}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Trailer registration country</Col>{" "}
                                <Col>
                                  {data.trailer_registration_country?.name ??
                                    "-"}
                                </Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Vehicle type</Col>{" "}
                                <Col>{data.vehicle_type.name}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Length (m)</Col>{" "}
                                <Col>{data.vehicle_length ?? "-"}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Width (m)</Col>{" "}
                                <Col>{data.vehicle_width ?? "-"}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Weight (T)</Col>{" "}
                                <Col>{data.vehicle_weight ?? "-"}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Trailer type</Col>{" "}
                                <Col>
                                  {data.trailer_type
                                    ? data.trailer_type.name
                                    : "-"}
                                </Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Plug-in</Col>{" "}
                                <Col>
                                  {data.plug_in !== null
                                    ? data.plug_in
                                      ? "Yes"
                                      : "No"
                                    : "-"}
                                </Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Temperature</Col>{" "}
                                <Col>{data.temperature || "-"}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Cargo type</Col>{" "}
                                <Col>
                                  {data.cargo_type ? (
                                    <CargoBadge type={data.cargo_type} />
                                  ) : (
                                    "-"
                                  )}
                                </Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Driver count</Col>{" "}
                                <Col>{data.drivers.length}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Customer reference</Col>{" "}
                                <Col>{data.customer_reference || "-"}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Booking reference</Col>{" "}
                                <Col>{data.booking_reference || "-"}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Request date</Col>{" "}
                                <Col>{formatDateTime(data.ordered_at)}</Col>
                              </Row>
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <Row>
                                <Col>Last update</Col>{" "}
                                <Col>{formatDateTime(data.updated_at)}</Col>
                              </Row>
                            </ListGroup.Item>
                            {data.archived_at && (
                              <ListGroup.Item>
                                <Row>
                                  <Col>Archived at</Col>{" "}
                                  <Col>{formatDateTime(data.archived_at)}</Col>
                                </Row>
                              </ListGroup.Item>
                            )}
                            <ListGroup.Item>
                              <Row>
                                <Col>Created by</Col>{" "}
                                <Col>{data.creator.name}</Col>
                              </Row>
                            </ListGroup.Item>
                          </ListGroup>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {data &&
                  data.drivers.map((driver, index) => (
                    <Row key={driver.id}>
                      <Col>
                        <Card className={styles.driverCard}>
                          <Card.Body>
                            <Card.Title>
                              <BsPerson /> {index === 0 ? "First" : "Second"}{" "}
                              driver
                            </Card.Title>

                            <hr />

                            <div>
                              <ListGroup
                                className={styles.driverData}
                                variant="flush"
                              >
                                <ListGroup.Item>
                                  <span>First name:</span>
                                  <span>{driver.first_name || "-"}</span>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                  <span>Last name:</span>
                                  <span>{driver.last_name || "-"}</span>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                  <span>Phone:</span>
                                  <span>{driver.phone || "-"}</span>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                  <span>Birth date:</span>
                                  <span>{driver.birth_date || "-"}</span>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                  <span>Document number:</span>
                                  <span>{driver.document_number || "-"}</span>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                  <span>Document country:</span>
                                  <span>
                                    {driver.document_country?.name || "-"}
                                  </span>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                  <span>Document expires:</span>
                                  <span>
                                    {driver.document_expiration_date || "-"}
                                  </span>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                  <span>Gender:</span>
                                  <span>
                                    {driver.gender
                                      ? driver.gender === 1
                                        ? "Female"
                                        : "Male"
                                      : "-"}
                                  </span>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                  <span>Nationality:</span>
                                  <span>{driver.country?.name || "-"}</span>
                                </ListGroup.Item>
                              </ListGroup>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  ))}
              </Col>
              {data && (
                <Col>
                  <BookingQuickView
                    sticky
                    showHeader
                    departureCountry={data.route.departure_country.name}
                    arrivalCountry={data.route.arrival_country.name}
                    departureAt={data.departure_at}
                    arrivalAt={data.arrival_at}
                    duration={data.duration}
                    company={data.route.company_name}
                    notes={data.route.notes}
                    isTransit={data.route.is_transit}
                  />

                  {data.documents.length > 0 && (
                    <Card className={styles.documentCard}>
                      <Card.Body>
                        <Card.Title>
                          <BsFiles /> Documents
                        </Card.Title>

                        <hr />

                        <ListGroup className={styles.documents} variant="flush">
                          {data.documents.map((document, index) => (
                            <ListGroup.Item
                              key={index}
                              className={styles.documentItem}
                            >
                              <span
                                onClick={() => download(document)}
                                className={`${styles.documentName} link-primary`}
                              >
                                <BsFillFilePdfFill className="text-danger" />{" "}
                                {document.name}
                              </span>
                              <OverlayTrigger
                                placement="top"
                                overlay={(props) => (
                                  <Tooltip
                                    id={`booking-document-tooltip-${document.id}`}
                                    {...props}
                                  >
                                    Uploaded:{" "}
                                    {formatDateTime(document.created_at)}
                                  </Tooltip>
                                )}
                              >
                                <span>{document.custom_properties.type}</span>
                              </OverlayTrigger>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      </Card.Body>
                    </Card>
                  )}
                </Col>
              )}
            </Row>
          </Container>
        </Modal.Body>
      </Modal>

      <ConfirmModal
        question="Cancel booking?"
        submitText="Yes"
        isVisible={isCancelConfirmVisible}
        onHide={() => {
          setIsCancelConfirmVisible(false);
          setShow(true);
        }}
        onSubmit={() => {
          cancel(Number(id));
          setIsCancelConfirmVisible(false);
          setShow(true);
        }}
      />

      <FilesModal
        booking={data}
        isVisible={isFilesVisible}
        onHide={() => {
          setIsFilesVisible(false);
          setShow(true);
        }}
      />
    </>
  );
};

export default BookingShow;
